import { useMemo, useState } from 'react';
import { BarcodeScannerDialog } from '@/components/barcode';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { playBarcodeScanBeep, normalizeBarcodeValue } from '@/lib/barcodeScan';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, ScanLine } from 'lucide-react';

export interface BatchSelectOption {
  id: string;
  batchNumber: string;
  quantityRemaining: number;
  expiryDate: string | null;
  productId?: string;
  productSku?: string | null;
  productName?: string | null;
}

export interface BatchSelectProps {
  /** Option id when matchValue is `id`, otherwise batch number. */
  value: string;
  onValueChange: (value: string, option?: BatchSelectOption) => void;
  batches: BatchSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  triggerClassName?: string;
  contentClassName?: string;
  /** Match selected value by batch number (default) or unique option id. */
  matchValue?: 'batchNumber' | 'id';
  scanDescription?: string;
}

function formatExpiryLabel(expiryDate: string | null): string {
  if (!expiryDate) return 'No expiry';
  return `Exp ${new Date(expiryDate).toLocaleDateString('en-GB')}`;
}

function filterBatchesByQuery(batches: BatchSelectOption[], query: string): BatchSelectOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return batches;
  return batches.filter((b) => {
    const parts = [
      b.batchNumber,
      b.productSku ?? '',
      b.productName ?? '',
    ];
    return parts.some((part) => part.toLowerCase().includes(q));
  });
}

function findSelectedOption(
  batches: BatchSelectOption[],
  value: string,
  matchValue: 'batchNumber' | 'id'
): BatchSelectOption | undefined {
  if (!value.trim()) return undefined;
  if (matchValue === 'id') {
    return batches.find((b) => b.id === value);
  }
  return batches.find((b) => b.batchNumber.toLowerCase() === value.trim().toLowerCase());
}

function isOptionSelected(
  batch: BatchSelectOption,
  value: string,
  selected: BatchSelectOption | undefined,
  matchValue: 'batchNumber' | 'id'
): boolean {
  if (matchValue === 'id') {
    return selected?.id === batch.id;
  }
  return value.trim().toLowerCase() === batch.batchNumber.toLowerCase();
}

function matchBatchByNumber(batches: BatchSelectOption[], raw: string): BatchSelectOption | undefined {
  const normalized = normalizeBarcodeValue(raw).toLowerCase();
  if (!normalized) return undefined;
  return batches.find((b) => b.batchNumber.toLowerCase() === normalized);
}

export function BatchSelect({
  value,
  onValueChange,
  batches,
  placeholder = 'Search or scan batch…',
  disabled,
  id,
  triggerClassName,
  contentClassName,
  matchValue = 'batchNumber',
  scanDescription = 'Scan the supplier batch label to select stock for this transfer line.',
}: BatchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [scannerOpen, setScannerOpen] = useState(false);

  const selected = findSelectedOption(batches, value, matchValue);

  const filteredBatches = useMemo(
    () => filterBatchesByQuery(batches, search),
    [batches, search]
  );

  const selectBatch = (batch: BatchSelectOption, options?: { beep?: boolean }) => {
    onValueChange(matchValue === 'id' ? batch.id : batch.batchNumber, batch);
    if (options?.beep) playBarcodeScanBeep();
    setOpen(false);
    setSearch('');
  };

  const handleSearchChange = (next: string) => {
    setSearch(next);
    const exact = matchBatchByNumber(batches, next);
    if (exact) {
      selectBatch(exact, { beep: true });
    }
  };

  const handleScan = (scanned: string) => {
    const normalized = normalizeBarcodeValue(scanned);
    setSearch(normalized);
    const match = matchBatchByNumber(batches, normalized);
    if (match) {
      selectBatch(match, { beep: false });
      return;
    }
    onValueChange(normalized);
    setOpen(true);
  };

  const displayValue =
    selected?.batchNumber
    ?? (matchValue === 'batchNumber' && value.trim() ? value.trim() : null)
    ?? (value.trim() || null);
  const selectedMeta = selected
    ? `${formatExpiryLabel(selected.expiryDate)} · ${selected.quantityRemaining} avail`
    : null;

  return (
    <>
      <div className={cn('flex w-full items-stretch', triggerClassName)}>
        <Popover
          open={open}
          onOpenChange={(next) => {
            setOpen(next);
            if (!next) setSearch('');
          }}
        >
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              title={selectedMeta ?? undefined}
              className={cn(
                'h-10 min-w-0 flex-1 justify-between rounded-r-none border-r-0 px-3 font-normal',
                !displayValue && 'text-muted-foreground'
              )}
            >
              <span className="flex flex-1 items-center min-w-0 text-left">
                {displayValue ? (
                  <span className="truncate font-mono text-sm">{displayValue}</span>
                ) : (
                  <span className="truncate text-sm">{placeholder}</span>
                )}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              'min-w-[min(calc(100vw-2rem),20rem)] w-[max(var(--radix-popover-trigger-width),18rem)] p-0',
              contentClassName
            )}
            align="start"
          >
            <Command shouldFilter={false}>
              <CommandInput
                value={search}
                onValueChange={handleSearchChange}
                placeholder="Batch number…"
                className="font-mono"
              />
              <CommandList>
                <CommandEmpty>No batches in stock.</CommandEmpty>
                <CommandGroup>
                  {filteredBatches.map((batch) => (
                      <CommandItem
                        key={batch.id}
                        value={batch.id}
                        onSelect={() => selectBatch(batch)}
                        className="py-2.5"
                      >
                        <Check
                          className={cn(
                            'mr-1 h-4 w-4 shrink-0',
                            isOptionSelected(batch, value, selected, matchValue)
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-mono text-sm">{batch.batchNumber}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground font-sans">
                            {batch.productSku && batch.productName
                              ? `${batch.productSku} – ${batch.productName} · `
                              : ''}
                            {batch.quantityRemaining} avail · {formatExpiryLabel(batch.expiryDate)}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        <Button
          type="button"
          variant="outline"
          size="icon"
          disabled={disabled}
          className="h-10 shrink-0 rounded-l-none px-3 text-muted-foreground hover:text-foreground"
          aria-label="Scan batch barcode"
          onClick={() => setScannerOpen(true)}
        >
          <ScanLine className="h-4 w-4" />
        </Button>
      </div>

      <BarcodeScannerDialog
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        title="Scan batch barcode"
        description={scanDescription}
        onScan={handleScan}
      />
    </>
  );
}
